import { Logo } from "components/Logo";
import { useEffect, useState } from "react";

/* Headline word that cycles — mirrors the marketing site's WordRotate hero. */
const ROTATING_WORDS = ["confidence", "clarity", "speed", "ease"];

/* Social-proof stats — same claims as the marketing site hero. */
const STATS = [
	{ target: 500, suffix: "+", label: "Accounting pros" },
	{ target: 10, suffix: "K+", label: "Reconciliations" },
	{ target: 50, suffix: "+", label: "Hours saved / mo" },
];

const WORD_ROTATE_MS = 2600;
const COUNT_UP_MS = 1600;

function usePrefersReducedMotion(): boolean {
	const [reduced, setReduced] = useState(
		() =>
			typeof window !== "undefined" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches,
	);

	useEffect(() => {
		const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
		const handler = () => setReduced(mq.matches);
		mq.addEventListener("change", handler);
		return () => mq.removeEventListener("change", handler);
	}, []);

	return reduced;
}

function RotatingWord({ paused }: { paused: boolean }) {
	const [index, setIndex] = useState(0);

	useEffect(() => {
		if (paused) return;
		const id = setInterval(() => {
			setIndex((i) => (i + 1) % ROTATING_WORDS.length);
		}, WORD_ROTATE_MS);
		return () => clearInterval(id);
	}, [paused]);

	return (
		<span className="auth-brand__rotator">
			{/* key re-mounts the word so the enter animation replays each cycle */}
			<em key={index} className="auth-brand__rotator-word">
				{ROTATING_WORDS[index]}
			</em>
		</span>
	);
}

function useCountUp(target: number, animate: boolean): number {
	const [value, setValue] = useState(animate ? 0 : target);

	useEffect(() => {
		if (!animate) {
			setValue(target);
			return;
		}

		let raf = 0;
		let start: number | null = null;
		const step = (ts: number) => {
			if (start === null) start = ts;
			const progress = Math.min((ts - start) / COUNT_UP_MS, 1);
			const eased = 1 - (1 - progress) ** 3;
			setValue(Math.floor(eased * target));
			if (progress < 1) raf = requestAnimationFrame(step);
		};
		raf = requestAnimationFrame(step);
		return () => cancelAnimationFrame(raf);
	}, [target, animate]);

	return value;
}

function StatCard({
	target,
	suffix,
	label,
	animate,
}: {
	target: number;
	suffix: string;
	label: string;
	animate: boolean;
}) {
	const value = useCountUp(target, animate);

	return (
		<div className="auth-brand__stat">
			<span className="auth-brand__stat-value">
				{value}
				{suffix}
			</span>
			<span className="auth-brand__stat-label">{label}</span>
		</div>
	);
}

export function AuthBrandPanel() {
	const reducedMotion = usePrefersReducedMotion();

	return (
		<aside className="auth-brand">
			<div className="auth-brand__logo">
				<Logo width={150} />
			</div>

			<div className="auth-brand__body">
				<span className="auth-brand__eyebrow">
					<span className="auth-brand__glow-dot" />
					<span className="auth-brand__shine">
						Trusted by 500+ accounting teams
					</span>
				</span>

				<h1 className="auth-brand__headline">
					Close your books
					<br />
					with <RotatingWord paused={reducedMotion} />
				</h1>

				<p className="auth-brand__subtitle">
					Automated bank reconciliation and financial review for QuickBooks
					Online — matched, verified, and closed in minutes.
				</p>

				<div className="auth-brand__stats">
					{STATS.map((stat) => (
						<StatCard
							key={stat.label}
							target={stat.target}
							suffix={stat.suffix}
							label={stat.label}
							animate={!reducedMotion}
						/>
					))}
				</div>
			</div>

			<div className="auth-brand__spacer" />
		</aside>
	);
}
